import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import PageLayout from '../../layouts/PageLayout';
import { payrollAPI } from '../../services';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { useUser, useBranch } from '../../hooks';
import PaginationComponent from '../../components/PaginationComponent';
import PayrollDetailModal from '../../components/modals/PayrollDetailModal';
import MonthPickerComponent from '../../components/fields/MonthPickerComponent';

// #175506 — CONFIRMED nằm giữa APPROVED và PAID: nhân viên đã tự bấm xác nhận lương đúng.
const PAYROLL_STATUS = ['DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'CONFIRMED', 'PAID'];

const STATUS_LABELS = {
  DRAFT: 'Nháp',
  PENDING_APPROVAL: 'Chờ duyệt',
  APPROVED: 'Đã duyệt',
  CONFIRMED: 'Nhân viên đã xác nhận',
  PAID: 'Đã thanh toán',
};

const statusVariant = {
  DRAFT: 'secondary',
  PENDING_APPROVAL: 'warning',
  APPROVED: 'info',
  CONFIRMED: 'primary',
  PAID: 'success',
};

const formatCurrency = (value) => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(Number(value));
};

const formatMonth = (month) => {
  if (!month) return '-';
  try {
    const [year, monthNum] = month.split('-');
    return `${monthNum}/${year}`;
  } catch {
    return month;
  }
};

export default function PayrollListPage() {
  const { user } = useUser();
  // #175019 — chi nhánh chỉ chọn ở thanh trên đầu; trang không còn ô lọc riêng.
  const { selectedBranch } = useBranch();
  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [filters, setFilters] = useState({
    month: format(new Date(), 'yyyy-MM'),
    status: '',
    employee_name: '',
    branch_id: selectedBranch?.id || '',
  });
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendingPayrolls, setSendingPayrolls] = useState(false);
  const [sendMethod, setSendMethod] = useState('telegram'); // 'telegram' or 'manual'

  const fetchPayrolls = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        ...(filters.month ? { month: filters.month } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.employee_name ? { employee_name: filters.employee_name } : {}),
        ...(filters.branch_id ? { branch_id: filters.branch_id } : {}),
        page,
        limit,
      };

      const response = await payrollAPI.getPayrolls(params);
      const payload = response?.data || response;
      const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];

      setPayrolls(list);
      setMeta(payload?.meta || null);
    } catch (error) {
      console.error('Cannot fetch payrolls', error);
      toast.error(error?.message || 'Không tải được danh sách bảng lương');
    } finally {
      setLoading(false);
    }
  }, [filters, page, limit]);

  useEffect(() => {
    fetchPayrolls();
  }, [fetchPayrolls]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, branch_id: selectedBranch?.id || '' }));
    setPage(1);
  }, [selectedBranch]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
    setSelectedIds(new Set());
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSelectedIds(new Set());
  };

  const handleViewDetail = (payroll) => {
    setSelectedPayroll(payroll);
    setShowDetailModal(true);
  };

  const handleCalculateAll = async () => {
    if (!filters.month) {
      toast.error('Vui lòng chọn tháng');
      return;
    }

    if (!window.confirm(`Bạn có chắc muốn tính lương cho tất cả nhân viên tháng ${formatMonth(filters.month)}?`)) {
      return;
    }

    try {
      setLoading(true);
      await payrollAPI.calculateAll({ month: filters.month });
      toast.success('Tính lương thành công');
      fetchPayrolls();
    } catch (error) {
      console.error('Cannot calculate all payrolls', error);
      toast.error(error?.message || 'Không thể tính lương cho tất cả nhân viên');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = new Set(payrolls.map((p) => p.id));
      setSelectedIds(allIds);
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (payrollId, checked) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(payrollId);
    } else {
      newSelected.delete(payrollId);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) {
      toast.warning('Vui lòng chọn ít nhất một bảng lương');
      return;
    }

    const selectedPayrolls = payrolls.filter((p) => selectedIds.has(p.id));
    const canApprove = selectedPayrolls.filter((p) => p.status === 'DRAFT' || p.status === 'PENDING_APPROVAL');

    if (canApprove.length === 0) {
      toast.warning('Không có bảng lương nào có thể duyệt (chỉ duyệt được DRAFT hoặc PENDING_APPROVAL)');
      return;
    }

    if (!window.confirm(`Bạn có chắc muốn duyệt ${canApprove.length} bảng lương đã chọn?`)) {
      return;
    }

    if (!user?.id) {
      toast.error('Không xác định được người duyệt');
      return;
    }

    try {
      setBulkActionLoading(true);
      const results = await Promise.allSettled(
        canApprove.map((payroll) => payrollAPI.approve(payroll.id, { current_user_id: user.id })),
      );

      const successCount = results.filter((r) => r.status === 'fulfilled').length;
      const failCount = results.length - successCount;

      if (successCount > 0) {
        toast.success(`Đã duyệt thành công ${successCount} bảng lương`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} bảng lương duyệt thất bại`);
      }

      setSelectedIds(new Set());
      fetchPayrolls();
    } catch (error) {
      console.error('Cannot bulk approve', error);
      toast.error(error?.message || 'Không thể duyệt bảng lương');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkMarkPaid = async () => {
    if (selectedIds.size === 0) {
      toast.warning('Vui lòng chọn ít nhất một bảng lương');
      return;
    }

    const selectedPayrolls = payrolls.filter((p) => selectedIds.has(p.id));
    // #175506 — chỉ trả lương sau khi CHÍNH NHÂN VIÊN xác nhận lương đúng.
    const canMarkPaid = selectedPayrolls.filter((p) => p.status === 'CONFIRMED');

    if (canMarkPaid.length === 0) {
      toast.warning('Không có bảng lương nào có thể đánh dấu đã thanh toán (chỉ đánh dấu được khi nhân viên đã xác nhận)');
      return;
    }

    // #175506 (bổ sung sếp `ul_anh_canhbao`): thiếu ảnh chuyển khoản chỉ CẢNH BÁO, KHÔNG chặn.
    const thieuAnh = canMarkPaid.filter((p) => !(p.payment_evidence_urls || []).length);
    const nhacThieuAnh = thieuAnh.length
      ? `\n\n⚠️ ${thieuAnh.length} bảng lương chưa có ảnh chuyển khoản — vẫn đánh dấu được, nhớ bổ sung ảnh sau.`
      : '';

    if (
      !window.confirm(
        `Bạn có chắc muốn đánh dấu đã thanh toán cho ${canMarkPaid.length} bảng lương đã chọn?${nhacThieuAnh}`,
      )
    ) {
      return;
    }

    if (!user?.id) {
      toast.error('Không xác định được người đánh dấu');
      return;
    }

    try {
      setBulkActionLoading(true);
      const results = await Promise.allSettled(
        canMarkPaid.map((payroll) => payrollAPI.markPaid(payroll.id, { current_user_id: user.id })),
      );

      const successCount = results.filter((r) => r.status === 'fulfilled').length;
      const failCount = results.length - successCount;

      if (successCount > 0) {
        toast.success(`Đã đánh dấu thành công ${successCount} bảng lương`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} bảng lương đánh dấu thất bại`);
      }
      if (thieuAnh.length > 0) {
        toast.warning(
          `${thieuAnh.length} bảng lương đã trả khi CHƯA có ảnh chuyển khoản — mở "Xem chi tiết" để bổ sung ảnh.`,
        );
      }

      setSelectedIds(new Set());
      fetchPayrolls();
    } catch (error) {
      console.error('Cannot bulk mark paid', error);
      toast.error(error?.message || 'Không thể đánh dấu đã thanh toán');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkSend = () => {
    if (selectedIds.size === 0) {
      toast.warning('Vui lòng chọn ít nhất một bảng lương');
      return;
    }
    setShowSendModal(true);
  };

  const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleConfirmSend = async () => {
    if (selectedIds.size === 0) {
      toast.warning('Vui lòng chọn ít nhất một bảng lương');
      return;
    }

    const selectedPayrolls = payrolls.filter((p) => selectedIds.has(p.id));

    try {
      setSendingPayrolls(true);

      if (sendMethod === 'telegram') {
        // Gửi qua Telegram - backend có thể trả về JSON (thành công) hoặc file CSV (lỗi Telegram)
        let successCount = 0;
        let failCount = 0;
        const downloadFiles = [];

        for (const payroll of selectedPayrolls) {
          try {
            const result = await payrollAPI.exportPayroll(payroll.id, {
              format: 'csv',
              send_telegram: true,
            });

            // Kiểm tra xem là JSON (thành công) hay Blob (file CSV - lỗi Telegram)
            if (result instanceof Blob) {
              // Lỗi Telegram, backend đã fallback về file CSV
              downloadFiles.push({ blob: result, payroll });
              failCount++;
            } else if (result && typeof result === 'object' && result.sent_to_telegram) {
              // Thành công gửi qua Telegram
              successCount++;
            } else {
              failCount++;
            }
          } catch (error) {
            console.error(`Error sending payroll ${payroll.id}:`, error);
            failCount++;
          }
        }

        // Download các file CSV fallback (lỗi Telegram)
        for (const { blob, payroll } of downloadFiles) {
          const employeeName = payroll.employee?.full_name || payroll.employee?.username || payroll.employee_id;
          const month = payroll.month || 'unknown';
          const filename = `bang_luong_${employeeName}_${month}.csv`.replace(/[^a-z0-9_]/gi, '_');
          downloadFile(blob, filename);
        }

        if (successCount > 0) {
          toast.success(`Đã gửi qua Telegram thành công ${successCount} bảng lương`);
        }
        if (downloadFiles.length > 0) {
          toast.warning(`${downloadFiles.length} bảng lương không thể gửi qua Telegram, đã tải file CSV thay thế`);
        }
        if (failCount > downloadFiles.length) {
          toast.error(`${failCount - downloadFiles.length} bảng lương xử lý thất bại`);
        }
      } else {
        // Gửi tay - download file CSV
        for (const payroll of selectedPayrolls) {
          try {
            const blob = await payrollAPI.exportPayroll(payroll.id, {
              format: 'csv',
              send_telegram: false,
            });

            const employeeName = payroll.employee?.full_name || payroll.employee?.username || payroll.employee_id;
            const month = payroll.month || 'unknown';
            const filename = `bang_luong_${employeeName}_${month}.csv`.replace(/[^a-z0-9_]/gi, '_');

            downloadFile(blob, filename);
          } catch (error) {
            console.error(`Error downloading payroll ${payroll.id}:`, error);
            toast.error(`Không thể tải bảng lương của ${payroll.employee?.full_name || payroll.employee_id}`);
          }
        }

        toast.success(`Đã tải xuống ${selectedPayrolls.length} file CSV`);
      }

      setShowSendModal(false);
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Cannot send payrolls', error);
      toast.error(error?.message || 'Không thể gửi bảng lương');
    } finally {
      setSendingPayrolls(false);
    }
  };

  const allSelected = payrolls.length > 0 && selectedIds.size === payrolls.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < payrolls.length;

  const summary = React.useMemo(() => {
    const totalSalary = payrolls.reduce((sum, p) => sum + (Number(p.final_salary) || 0), 0);
    const byStatus = {};
    PAYROLL_STATUS.forEach((s) => {
      byStatus[s] = payrolls.filter((p) => p.status === s).length;
    });
    return {
      totalSalary,
      count: payrolls.length,
      approved: (byStatus.APPROVED || 0) + (byStatus.CONFIRMED || 0),
      paid: byStatus.PAID || 0,
      pending: (byStatus.DRAFT || 0) + (byStatus.PENDING_APPROVAL || 0),
    };
  }, [payrolls]);

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-breadcrumb">
              <h3 className="mc-breadcrumb-title">Quản lý bảng lương</h3>
            </div>
          </div>
        </Col>

        <Col xl={12}>
          <Row className="mb-3 g-3">
            <Col md={6} xl={3}>
              <Card className="shadow-sm border-primary h-100">
                <Card.Body>
                  <div className="text-muted small text-uppercase mb-1">Tổng lương (trang hiện tại)</div>
                  <div className="fs-4 fw-bold text-primary">
                    {loading ? '...' : formatCurrency(summary.totalSalary)}
                  </div>
                  <div className="small text-muted">{summary.count} bảng lương</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={3}>
              <Card className="shadow-sm h-100">
                <Card.Body>
                  <div className="text-muted small text-uppercase mb-1">Chờ duyệt</div>
                  <div className="fs-4 fw-bold text-warning">{summary.pending}</div>
                  <div className="small text-muted">Draft + Chờ duyệt</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={3}>
              <Card className="shadow-sm h-100">
                <Card.Body>
                  <div className="text-muted small text-uppercase mb-1">Đã duyệt</div>
                  <div className="fs-4 fw-bold text-info">{summary.approved}</div>
                  <div className="small text-muted">Chưa thanh toán</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={3}>
              <Card className="shadow-sm h-100">
                <Card.Body>
                  <div className="text-muted small text-uppercase mb-1">Đã thanh toán</div>
                  <div className="fs-4 fw-bold text-success">{summary.paid}</div>
                  <div className="small text-muted">PAID</div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="shadow-sm mb-3">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Bộ lọc</h5>
                <Button
                  variant="primary"
                  onClick={handleCalculateAll}
                  disabled={loading}
                >
                  Tính lương tất cả
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col lg={3}>
                  <MonthPickerComponent
                    label="Tháng"
                    value={filters.month}
                    onChange={(value) => handleFilterChange('month', value)}
                  />
                </Col>
                <Col lg={3}>
                  <Form.Label>Trạng thái</Form.Label>
                  <Form.Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="">Tất cả</option>
                    {PAYROLL_STATUS.map((status) => (
                      <option
                        key={status}
                        value={status}
                      >
                        {STATUS_LABELS[status]}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col lg={3}>
                  <Form.Label>Tên nhân viên</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Nhập tên nhân viên"
                    value={filters.employee_name}
                    onChange={(e) => handleFilterChange('employee_name', e.target.value)}
                  />
                </Col>
                <Col lg={3}>
                  <Form.Label>Số dòng hiển thị</Form.Label>
                  <Form.Select
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setPage(1);
                      setSelectedIds(new Set());
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={0}>Tất cả (không giới hạn)</option>
                  </Form.Select>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="shadow-sm">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Danh sách bảng lương</h5>
                {selectedIds.size > 0 && (
                  <div className="d-flex gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleBulkApprove}
                      disabled={bulkActionLoading}
                    >
                      {bulkActionLoading ? (
                        <>
                          <Spinner
                            size="sm"
                            className="me-2"
                          />
                          Đang xử lý...
                        </>
                      ) : (
                        `Duyệt đã chọn (${selectedIds.size})`
                      )}
                    </Button>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={handleBulkMarkPaid}
                      disabled={bulkActionLoading}
                    >
                      {bulkActionLoading ? (
                        <>
                          <Spinner
                            size="sm"
                            className="me-2"
                          />
                          Đang xử lý...
                        </>
                      ) : (
                        `Đánh dấu đã thanh toán (${selectedIds.size})`
                      )}
                    </Button>
                    <Button
                      variant="info"
                      size="sm"
                      onClick={handleBulkSend}
                      disabled={bulkActionLoading || sendingPayrolls}
                    >
                      {sendingPayrolls ? (
                        <>
                          <Spinner
                            size="sm"
                            className="me-2"
                          />
                          Đang gửi...
                        </>
                      ) : (
                        `Gửi bảng lương (${selectedIds.size})`
                      )}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setSelectedIds(new Set())}
                    >
                      Bỏ chọn
                    </Button>
                  </div>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              {loading && (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              )}

              {!loading && payrolls.length === 0 && <div className="text-center py-4 text-muted">Không có dữ liệu</div>}

              {!loading && payrolls.length > 0 && (
                <>
                  <Table
                    responsive
                    hover
                  >
                    <thead>
                      <tr>
                        <th style={{ width: '50px' }}>
                          <Form.Check
                            type="checkbox"
                            checked={allSelected}
                            ref={(input) => {
                              if (input) input.indeterminate = someSelected;
                            }}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                          />
                        </th>
                        <th>Nhân viên</th>
                        <th>Tháng</th>
                        <th>Lương/giờ</th>
                        <th>Giờ quy đổi</th>
                        <th>Lương cuối cùng</th>
                        <th>Trạng thái</th>
                        <th>Thao tác</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payrolls.map((payroll) => (
                        <tr key={payroll.id}>
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={selectedIds.has(payroll.id)}
                              onChange={(e) => handleSelectOne(payroll.id, e.target.checked)}
                            />
                          </td>
                          <td>
                            {payroll.employee?.full_name || payroll.employee?.username || payroll.employee_id || '-'}
                          </td>
                          <td>{formatMonth(payroll.month)}</td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <span>{formatCurrency(payroll.hourly_rate)}/h</span>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => handleViewDetail(payroll)}
                                title="Chỉnh sửa lương giờ"
                              >
                                <i
                                  className="material-icons"
                                  style={{ fontSize: '16px' }}
                                >
                                  edit
                                </i>
                              </Button>
                            </div>
                          </td>
                          <td>{payroll.total_converted_hours || 0}h</td>
                          <td>{formatCurrency(payroll.final_salary)}</td>
                          <td>
                            <Badge bg={statusVariant[payroll.status] || 'secondary'}>
                              {STATUS_LABELS[payroll.status] || payroll.status}
                            </Badge>
                            {/* #175506 — soát lại được phiếu đã trả mà lúc đó chưa có ảnh chuyển khoản */}
                            {payroll.paid_without_evidence && (
                              <div className="mt-1">
                                <Badge bg="warning" text="dark" title="Đã đánh dấu thanh toán khi chưa có ảnh chuyển khoản">
                                  ⚠️ Thiếu ảnh lúc trả
                                </Badge>
                              </div>
                            )}
                          </td>
                          <td>
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() => handleViewDetail(payroll)}
                            >
                              Xem chi tiết
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {meta &&
                    (limit === 0 ? (
                      <div className="text-muted small px-2 py-2 border-top">
                        Hiển thị tất cả <strong>{meta.total ?? payrolls.length}</strong> bản ghi (không phân trang)
                      </div>
                    ) : (
                      <PaginationComponent
                        totalItems={meta.total || 0}
                        itemsPerPage={limit}
                        currentPage={page}
                        onPageChange={handlePageChange}
                        loading={loading}
                      />
                    ))}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {showDetailModal && selectedPayroll && (
        <PayrollDetailModal
          show={showDetailModal}
          onHide={() => {
            setShowDetailModal(false);
            setSelectedPayroll(null);
          }}
          payrollId={selectedPayroll.id}
          onRefresh={fetchPayrolls}
          currentUserId={user?.id}
        />
      )}

      {showSendModal && (
        <Modal
          show={showSendModal}
          onHide={() => setShowSendModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Gửi bảng lương</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <strong>Số lượng bảng lương đã chọn:</strong> {selectedIds.size}
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Phương thức gửi</Form.Label>
              <div>
                <Form.Check
                  type="radio"
                  label="Gửi qua Telegram (tự động)"
                  checked={sendMethod === 'telegram'}
                  onChange={() => setSendMethod('telegram')}
                  className="mb-2"
                />
                <Form.Check
                  type="radio"
                  label="Xuất file CSV (gửi tay)"
                  checked={sendMethod === 'manual'}
                  onChange={() => setSendMethod('manual')}
                />
              </div>
            </Form.Group>
            {sendMethod === 'telegram' && (
              <div className="alert alert-info mb-0">
                <strong>Lưu ý:</strong> Bảng lương sẽ được gửi tự động qua Telegram cho nhân viên tương ứng.
              </div>
            )}
            {sendMethod === 'manual' && (
              <div className="alert alert-warning mb-0">
                <strong>Lưu ý:</strong> File CSV sẽ được tải xuống, bạn có thể gửi thủ công cho nhân viên.
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowSendModal(false)}
              disabled={sendingPayrolls}
            >
              Hủy
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmSend}
              disabled={sendingPayrolls}
            >
              {sendingPayrolls ? (
                <>
                  <Spinner
                    size="sm"
                    className="me-2"
                  />
                  Đang xử lý...
                </>
              ) : (
                'Xác nhận'
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </PageLayout>
  );
}
